import React, { useCallback, useEffect, useState } from "react";

import { useApi } from "../hooks/useApi";
import { loadNotifications, markNotificationRead } from "../services/notifications";
import logger from "../utils/logger";
import "./NotificationBar.css";

const POLL_INTERVAL_MS = 60000;

const LEVEL_CLASS = {
  info: "notification-bar__item--info",
  success: "notification-bar__item--success",
  warning: "notification-bar__item--warning",
  error: "notification-bar__item--error",
};

// Разделы плашки. Событие попадает в раздел по event_type, остальное — в «Ключи и автоматизация».
const SECTIONS = [
  { key: "sales", title: "Продажи", events: ["orders."] },
  { key: "ads", title: "Реклама", events: ["ads."] },
  { key: "stock", title: "Остатки и логистика", events: ["stock."] },
  { key: "money", title: "Деньги и прибыль", events: ["ue.", "coinvest.", "billing."] },
  { key: "system", title: "Ключи и автоматизация", events: [] },
];

const sectionKeyFor = (item) => {
  const type = item.event_type || "";
  const found = SECTIONS.find((section) => section.events.some((prefix) => type.startsWith(prefix)));
  return found ? found.key : "system";
};

/**
 * Плашка уведомлений над рабочей областью: важное (предупреждения и ошибки) по разделам,
 * в каждом — самые свежие события и переход в нужный инструмент. Прочитанное скрывается.
 */
function NotificationBar({ token, onOpen }) {
  const { request } = useApi();
  const [items, setItems] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [openKey, setOpenKey] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const data = await loadNotifications(request, { unreadOnly: true, limit: 50 });
      // Верхняя плашка — только для важного (предупреждения и ошибки); остальное живёт в колокольчике.
      setItems((Array.isArray(data) ? data : []).filter((item) => item.level === "warning" || item.level === "error"));
    } catch (error) {
      logger.error(error);
    }
  }, [request, token]);

  useEffect(() => {
    if (!token) {
      setItems([]);
      return undefined;
    }
    refresh();
    const timer = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [token, refresh]);

  const markRead = async (list) => {
    const ids = new Set(list.map((item) => item.id));
    await Promise.all(
      list.map((item) => markNotificationRead(request, item.id).catch((error) => logger.error(error)))
    );
    setItems((prev) => prev.filter((item) => !ids.has(item.id)));
  };

  const handleOpen = async (notification) => {
    if (notification.link && onOpen) {
      onOpen(notification.link);
    }
    setBusyKey(notification.id);
    try {
      await markRead([notification]);
    } finally {
      setBusyKey(null);
    }
  };

  const dismissSection = async (section, list) => {
    setBusyKey(section.key);
    try {
      await markRead(list);
    } finally {
      setBusyKey(null);
    }
  };

  if (!token || items.length === 0) return null;

  const groups = SECTIONS
    .map((section) => ({ section, list: items.filter((item) => sectionKeyFor(item) === section.key) }))
    .filter((group) => group.list.length > 0);

  const expanded = groups.find((group) => group.section.key === openKey);

  return (
    <section className="notification-bar" role="status" aria-live="polite">
      {groups.map(({ section, list }) => {
        const item = list[0];
        const isOpen = section.key === openKey;
        return (
          <div className={`notification-bar__cell ${LEVEL_CLASS[item.level] || LEVEL_CLASS.info}`} key={section.key}>
            <button
              type="button"
              className="notification-bar__open"
              title={`${item.title}. ${item.message}`}
              aria-expanded={isOpen}
              onClick={() => setOpenKey(isOpen ? null : section.key)}
            >
              <span className="notification-bar__section-title">{section.title}</span>
              <span className="notification-bar__counter">{list.length}</span>
              <span className="notification-bar__title">{item.title}</span>
              <span className="notification-bar__arrow">{isOpen ? "▴" : "▾"}</span>
            </button>
            <button
              type="button"
              className="notification-bar__close"
              aria-label={`Скрыть уведомления раздела «${section.title}»`}
              title="Скрыть раздел"
              onClick={() => dismissSection(section, list)}
              disabled={busyKey === section.key}
            >
              ×
            </button>
          </div>
        );
      })}
      {expanded && (
        <ul className="notification-bar__list">
          {expanded.list.map((item) => (
            <li className={`notification-bar__row ${LEVEL_CLASS[item.level] || LEVEL_CLASS.info}`} key={item.id}>
              <div className="notification-bar__row-text">
                <strong className="notification-bar__row-title">{item.title}</strong>
                <span className="notification-bar__message">{item.message}</span>
              </div>
              {item.link && (
                <button
                  type="button"
                  className="notification-bar__button notification-bar__button--primary"
                  onClick={() => handleOpen(item)}
                  disabled={busyKey === item.id}
                >
                  Открыть →
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default NotificationBar;
